/**
 * CredentialPickerDialog — modal to browse and select a keybook credential.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Search, Key, BookKey } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

interface CredentialEntry {
  id: string
  label: string
  credentialType?: string
  usernameHint?: string | null
  fileId?: string | null
  compatible: boolean
}

interface CredentialPickerDialogProps {
  entries: CredentialEntry[]
  activeEntryId?: string
  onSelect: (entryId: string, compatible: boolean) => void
  onClose: () => void
}

export function CredentialPickerDialog({ entries, activeEntryId, onSelect, onClose }: CredentialPickerDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [filter, setFilter] = useState('')

  const filtered = filter
    ? entries.filter(e => e.label.toLowerCase().includes(filter.toLowerCase()) || (e.usernameHint || '').toLowerCase().includes(filter.toLowerCase()))
    : entries

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative z-10 w-full max-w-sm rounded-xl shadow-xl themed-bg-secondary`}>
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b themed-border">
          <BookKey size={16} className="themed-icon" />
          <span className="text-sm font-medium themed-text flex-1">Select Credential</span>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b themed-border">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="themed-input w-full pl-8 pr-3 py-1.5 rounded-lg text-sm"
              placeholder="Search credentials..."
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <div className="max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs themed-text-muted">
              {filter ? 'No matching credentials' : 'No credentials saved'}
            </div>
          ) : (
            filtered.map(entry => {
              const isActive = entry.id === activeEntryId
              return (
                <button
                  key={entry.id}
                  onClick={() => onSelect(entry.id, entry.compatible)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b themed-border ${
                    isActive ? 'bg-yellow-600/15' : 'hover:themed-bg-tertiary'
                  }`}
                >
                  <Key size={14} className={
                    isActive ? 'text-yellow-400' : entry.compatible ? 'text-yellow-400/50' : 'text-zinc-600'
                  } />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${isActive ? 'text-yellow-300' : entry.compatible ? 'themed-text' : 'themed-text-muted opacity-50'}`}>
                      {entry.label}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] themed-text-muted">
                      {entry.usernameHint && <span>{entry.usernameHint}</span>}
                      {entry.credentialType && <span>{entry.credentialType}</span>}
                      {!entry.compatible && <span className="text-red-400">incompatible</span>}
                    </div>
                  </div>
                  {isActive && <span className="text-[10px] text-yellow-400">selected</span>}
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
